/**
 * 「超时」作业行 —— 家长端与学生端**共用的同一个组件**。
 *
 * ## 为什么抽出来而不是各写一份
 *
 * 「超时」在两个页面都要出现（家长端看有没有欠账并决定放弃，学生端接着做），
 * 而它的**口径**必须完全一样：同样的排序（服务端 `dueAt` 倒序，前端不重排）、
 * 同一句"已超时 X"、同一句"仍然可以做"。用户原话是「没有完成的作业任务，
 * 会一直显示，在「超时」标签中显示，直到完成或家长点击放弃」——
 * 如果两端各写一个行组件，这些文案迟早漂移，家长与孩子看到的就不是同一件事了。
 *
 * 两端**唯一**的差别是「放弃」与「验收」这两组动作的入口：
 *   - **「放弃」**只出现在家长端（后端 `canSkipHomeworkCheckin` 也只允许家长
 *     owner/editor）→ 用**可选**的 `onSkip` 表达：
 *       - 不传 = 这一页没有「放弃」入口（学生端就是这么用的，刻意不传，
 *         而不是"传了但禁用"—— 不靠"点了会失败"来保护）；
 *       - 传了 = 渲染按钮（家长端）。
 *   - **「通过 / 打回」**（验收）同样只出现在家长端，同样用可选的
 *     `onApprove` / `onReject` 表达。⚠️ 2026-10-15 用户实测的 bug 就出在这里：
 *     验收动作当时**只**做在「今天的作业」那一支里，而**昨天提交的项只出现在
 *     超时列表** → 家长在唯一看得见它的地方**没有通过按钮**，可界面上却写着
 *     "这一项会在家长点「通过」后从超时里消失"。修法是这一行渲染
 *     **同一个** `ApprovalActions`（「今天的作业」里那条作业项用的就是它），
 *     而不是在超时列表里再写一份按钮。
 *
 * ## 三个按钮**同一行**（2026-10-15 第二次用户实测）
 *
 * 用户原话：
 *
 * > 那个红色的"放弃"按钮**排在第二行了**，你让他们三个按钮在同一行吧。
 *
 * 根因不是宽度不够，而是**两个按钮容器**：「通过 / 打回」在 `ApprovalActions`
 * 的按钮行里，「放弃」原先在下面那个 `mt-3 flex flex-wrap` 里 —— 两个容器
 * 必然各占一行。现在「放弃」由 `trailingActions` 插进**同一个**按钮行，
 * "同属一行"因此是**结构**保证的（窄屏真的放不下时才 `flex-wrap` 换行，
 * 见 `ApprovalActions` 的注释）。
 * ⛔ 不要"顺手"给「放弃」再包一层 div —— 那会重新造出第二个容器。
 * ⛔ 也不要恢复 `flex-1` 的 `flex-basis:0`（本项目为此修过好几次，见 `ApprovalActions`）。
 *
 * ## 三个按钮**一样高**（2026-10-15 第三次用户实测）
 *
 * 用户原话：
 *
 * > 三个按钮在一行了，但是"放弃"按钮好像比另外两个按钮高哦，你把它改成一样高的，
 * > 这样才好看呐。
 *
 * 同属一行只保证了**顶端**对齐；「通过 / 打回」是 `sm`（36px），而这里原先写的是
 * `size={kid ? 'kid' : 'md'}`（44px）—— 于是那个红按钮的**底端**比另外两个低 8px。
 * 现在它在这一行里读 `APPROVAL_ACTION_BUTTON_SIZE`（`ApprovalActions` 导出，
 * 与同行的两个按钮**同一个值**）；留在下面那块（与 `md` 的「现在做 / 暂停 / 完成」
 * 排一起）时才用 `md` / `kid`。⛔ 别把面板那一行改回 `md`（320px 下会换行，见
 * `ApprovalActions` 文件头"必须同 size"）。
 *
 * ## 超时行与"今天的作业"行长得不一样是刻意的
 *
 * 它的 `id` 直接来自队列（不走作业单），所以：
 *  - 必须写出**属于哪一天**（`entry.date`）与截止时刻，否则跨天的项看起来"没有出处"；
 *  - 必须明说**「仍然可以做」**，否则孩子会以为这一项已经废了，而它其实一直在等他；
 *  - 用琥珀色而不是红色 —— 红色像是在惩罚，而它其实还能做。
 *
 * ## 2026-10-17：同一个组件服务**两条**队列（`queue` prop）
 *
 * 用户报的 bug：周五布置、周日截止的两项作业，周六在「今天的作业」（按布置日筛）
 * 与「超时」（按截止时刻筛）里**都看不见**。修法是补一条互补的队列
 * 「**还有效**（还没到期）」—— 而它的每一行与本文件的超时行**动作完全相同**
 * （开始/暂停/完成/通过/打回/放弃）。
 *
 * 所以这里没有第二个行组件，只有 `queue: 'overdue' | 'open'` 这一个开关，
 * 差别仅限三处呈现：
 *   - 图标 `⏰` → `📌`；
 *   - 琥珀色 → **天蓝**（这一支的每一项都**还没到期**，染成"欠账色"是冤枉孩子）；
 *   - 日期那一行换成服务端算好的徽章 `📅 周五 10-16 布置 · 周日 10-18 截止`
 *     （**不是今天布置的**必须说出来，这是任务点名的一条；而且徽章不含时刻，
 *     绝不显示 `23:59` —— 那是 `dayEnd` 的默认值，见 `homework-due.ts`）。
 * ⛔ 不要为「还有效」另写一份按钮/验收面板：2026-10-15 那个"超时里没有通过按钮"
 * 的 bug 就是两个视图各写一份造成的。
 *
 * ## 2026-10-17（第二次）：「还有效」的行**看不到计时数字**
 *
 * 用户原话：
 *
 * > 孩子端这里点了暂停后，过一段时间想重新开始，就变成这样，**看不到那个计时的了**。
 *
 * 截图里那一项状态是「计时中」、按钮是「暂停 / 完成」，**唯独没有那个大的已用时间**。
 * 根因：那块 JSX 当时**只有两份手抄**（「今天的作业」的 `HomeworkItemRow`、
 * 今日打卡页的 `TaskRow`），而本文件这一行**没有抄** —— 它只写了一句
 * "正在计时中"，既没有数字、也确认不了计时状态。
 *
 * 修法不是"在这里补一份"（那就变成第三份），而是**抽出唯一组件**：
 * `components/ui/CheckinTimerPanel.tsx`（判据在 `@fht/shared` 的 `timer-panel.ts`），
 * 三条队列与今日打卡页渲染的是**同一个**它。本文件这一行只是把它放在
 * 日期徽章下面 —— `queue` 仍然只切换**配色**（`sky` / `amber`）与图标（📌 / ⏰）。
 *
 * 同一轮还补齐了两处"队列行有、今天的作业行也有"的东西：
 *  - **`家长说：…`**（打回原因）—— 被打回的项恰恰会留在队列里，而孩子以前在这一屏
 *    看不到"要改什么"；
 *  - **分值 / 目标量 / 预计时长** —— 并把 `targetValue` / `unit` 交给调用方，
 *    让队列行的「完成」走**同一个**完成弹窗与同一条"完成量必填"校验
 *    （以前调用方只能传 `null, null`，那条校验被绕过）。
 */
import { Badge } from '../ui/Card';
import { Button } from '../ui/Button';
import type { ButtonSize } from '../ui/Button';
import { CheckinTimerPanel } from '../ui/CheckinTimerPanel';
import { APPROVAL_ACTION_BUTTON_SIZE, ApprovalActions } from './ApprovalActions';
import {
  describeHomeworkOpenQueueText,
  formatDuration,
  formatOverdueDuration,
  homeworkApprovalActions,
  homeworkOverdueSeconds,
  isHomeworkActionable,
  isLiveTimerStatus,
  type CheckinStatus,
} from '@fht/shared';
import { cn, formatDateTimeLocal, formatPlannedDuration } from '../../lib/utils';
import { statusMeta } from '../ui/Bits';

/**
 * 一行超时作业需要的**全部**字段（家长端 `HomeworkOverdueEntry` 与学生端
 * 超时队列的条目都满足这个形状）。
 *
 * 刻意用结构类型而不是直接引 `lib/api/homework.ts` 的类型：
 * 这个组件的契约就是"给我这些字段"，两端的数据来源（作业接口 vs 超时接口）
 * 因此可以各自演化，而不会因为一个可选字段的增删把另一边拖下水。
 */
export interface OverdueRowEntry {
  /** **checkinId**（计时 / 完成 / 放弃 / **验收**都用它，不是 habitId）。 */
  id: string;
  habitName: string;
  subjectName: string;
  /** 这条作业属于哪一天（'YYYY-MM-DD'）。 */
  date: string;
  dueAt: string;
  status: CheckinStatus;
  overdueSeconds: number;
  /**
   * **服务端确认过的**已用秒数（计时的大号数字用它，见 `CheckinTimerPanel`）。
   *
   * ⚠️ 服务端在两条队列里给的都是用 `recompute_checkin_seconds()` **实时算过**的值
   * （不是 `checkin.actual_seconds` 那一列的旧快照 —— 那一列在 pause/resume/心跳时
   * 都不写回）。生产实测：某项暂停过一次、`actual_seconds` 还是 0，
   * 而 recompute 出来是 358 秒。所以这里⛔ 不要自己乘、也不要用 0 兜底成"没做过"。
   */
  actualSeconds: number;
  /** 「预计 X」用的秒数（2026-10-17 起队列行也显示它，与「今天的作业」一致）。 */
  estimatedSeconds?: number | null;
  /**
   * 作业项的目标量 / 单位（`null` = 没设目标）。
   *
   * ⚠️ 它们必须从**队列**里带上来：孩子从「超时 / 还有效」点「完成」时，用的是
   * 与「今天的作业」**同一个**完成弹窗；以前这里没有这两个字段，调用方只能传
   * `null, null` —— 于是"有目标值就必须自己填完成量"这条校验在队列行上被绕过。
   */
  targetValue?: number | null;
  unit?: string | null;
  needApproval: boolean;
  /** 家长放弃时填的原因（空字符串 = 没填）。 */
  skipReason: string;
  /**
   * 家长的话：**打回原因**（`reject` 会把理由写进 `checkin.note`）或完成时留的备注。
   *
   * ⚠️ 队列行以前**不显示**它（只有「今天的作业」那一支显示 "家长说：…"），
   * 而被打回的项（`submitted → pending`）恰恰会留在「超时 / 还有效」里 ——
   * 于是孩子在这两个视图里看不到"要改什么"。空字符串 = 没留言。
   */
  note?: string;
  /**
   * 服务端给的验收权限（`canApproveCheckin(actor)`）—— **只有家长端会拿到 true**。
   *
   * ⚠️ 它必须是**可选**的：学生端读的是同一个超时队列，但学生这一端
   * 从来不需要这个字段（后端对作业项返回的 `canApprove` 对学生恒 false）。
   * 用可选字段而不是"必填 + 学生端也得给"，是为了让**没有这个数据来源**的调用点
   * 保持原样 —— 缺省 = 不渲染验收按钮（宁可少一个按钮，也不给一个点了会失败的按钮）。
   */
  canApprove?: boolean;
  /**
   * 这一项的分值；`null` / `undefined` = 拿不到（**不吞成 0**：0 是"明确不计分"）。
   * 面板文案因此分三支，见 `@fht/shared` 的 `homeworkApprovalActions()`。
   */
  pointsPerUnit?: number | null;
}

export interface OverdueRowProps {
  entry: OverdueRowEntry;
  /**
   * 这一行属于**哪一条派生队列**（默认 `'overdue'`）。
   *
   *  - `'overdue'`（超时）：`due_at < now()`，琥珀色 + 「已超时 X」。
   *  - `'open'`（**还有效**，2026-10-17 新增）：`due_at >= now()` 且**已经不是今天布置的**。
   *    用户的 bug 就是这里：周五布置、周日截止的两项作业，周六在「今天的作业」
   *    （按布置日筛）和「超时」（按截止时刻筛）里**都看不见**，孩子连「完成」都点不到。
   *
   * ⚠️ 两条队列**复用同一个组件**是刻意的：动作（开始/暂停/完成/通过/打回/放弃）、
   * 权限判据、`ApprovalActions` 面板、失效缓存链因此只有一份 —— 上一次
   * "昨天提交的项在超时里没有通过按钮"（2026-10-15）就是两个视图各写一份造成的。
   * 差别只有三处**呈现**：图标、颜色、以及这句"还没到期"的说明文字。
   */
  queue?: 'overdue' | 'open';
  /**
   * `queue === 'open'` 时那条「哪天布置 · 哪天截止」的徽章（**服务端算好的逐字文案**，
   * 见 `@fht/shared` 的 `describeHomeworkOpenBadge()`）。
   *
   * 为什么由调用方给：它需要家庭时区的"今天"（决定说"今天截止"还是"周日 10-18 截止"），
   * 而这个值只有服务端有。缺省时这一行退回显示原始的 `date` + `dueAt`，
   * 至少不会把"布置日"说成"截止日"。
   */
  openBadge?: string;
  /** 儿童模式（学生端）：放大字号与点击区。 */
  kid?: boolean;
  busy?: boolean;
  /**
   * **这一端是不是"正在计时的那一端"**（学生端传 `true`，家长端不传）。
   *
   * 决定计时面板只是"显示服务端快照"还是"真的在走"：只有 `true` 才会挂
   * `useServerTimer` 的 1 秒 tick + 30 秒心跳 + 到点提示音。
   * 家长端必须保持默认的 `false` —— 心跳端点对家长返回 403（`canCheckin` 只有学生），
   * 让家长的浏览器去发心跳只会得到一串失败并把"网络不太好"显示在家长眼前。
   */
  live?: boolean;
  /**
   * 还能做的三项动作（学生端会传；家长端只传 `onSkip`，所以那边不渲染这些按钮）。
   * 全部可选 = 这个组件不会因为某一端少给一个回调就渲染出"点了没反应"的按钮。
   */
  onStart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onFinish?: () => void;
  /**
   * 「放弃」——**只有家长端会传**。不传时这一行完全没有放弃入口（学生端）。
   * 按钮只负责"打开确认弹窗"，真正的写操作在页面里（要先给人话确认）。
   */
  onSkip?: () => void;
  /** 家长端：当前使用者有没有放弃权限（`canSkipHomeworkCheckin` 的前端面）。 */
  canSkip?: boolean;
  /** 家长端：把「仍然可以做」那句换成"孩子做 / 你放弃"的说法（见下方注释）。 */
  parentView?: boolean;
  /**
   * **验收**（通过 / 打回）—— 家长端会传，学生端**刻意不传**（学生不能验收）。
   *
   * ⚠️ 这一对回调是 2026-10-15 那个 bug 的修复点：用户在「超时」里看到
   * "这一项会在家长点「通过」后从超时里消失"，却**没有「通过」按钮** ——
   * 因为验收动作当时只做在「今天的作业」那一支里，而**昨天提交的项只出现在超时列表**。
   *
   * 这里不自己渲染按钮，而是交给**同一个** `ApprovalActions`
   * （「今天的作业」里那条作业项用的就是它），所以显隐判据、文案、按钮样式
   * 两个视图**不可能漂移**。
   */
  onApprove?: () => void;
  onReject?: () => void;
}

export function OverdueRow({
  entry,
  queue = 'overdue',
  openBadge,
  kid = false,
  busy = false,
  live = false,
  onStart,
  onPause,
  onResume,
  onFinish,
  onSkip,
  canSkip = false,
  parentView = false,
  onApprove,
  onReject,
}: OverdueRowProps) {
  const meta = statusMeta(entry.status);
  /** 「还有效」≠「超时」：图标与颜色都必须能一眼分开（它没超时，别让人以为欠账）。 */
  const isOpen = queue === 'open';
  const isRunning = entry.status === 'running';
  const isPaused = entry.status === 'paused' || entry.status === 'auto_paused';
  const isSubmitted = entry.status === 'submitted';
  const actionable = isHomeworkActionable(entry.status);
  /**
   * 这一行有没有**计时面板**（`running` / `paused` / `auto_paused`）。
   *
   * ⚠️ 判据只有 `@fht/shared` 的 `isLiveTimerStatus()` 一份，面板内部用的也是它。
   * 这里单独取一个布尔量，是为了让下面"已超时 X · 已经做了 Y"那句在
   * 已经有**大号数字**时不再重复报一次时长（同一个数显示两遍、格式还不一样）。
   */
  const showTimer = isLiveTimerStatus(entry.status);
  /**
   * 「完成可得 N 分 · 目标 X · 预计 Y」那一行（与「今天的作业」的行同一组信息）。
   *
   * ⚠️ `pointsPerUnit` 缺席（`null`）时**什么都不说**：`null` 是"服务端没给"，
   * `0` 才是"明确不计分"（见 `HomeworkOverdueEntry.pointsPerUnit` 的注释）。
   */
  const entryInfoLine = [
    typeof entry.pointsPerUnit === 'number'
      ? entry.pointsPerUnit > 0
        ? `完成可得 ${entry.pointsPerUnit} 分`
        : '不记分'
      : null,
    entry.targetValue !== null && entry.targetValue !== undefined
      ? `目标 ${entry.targetValue}${entry.unit ?? ''}`
      : null,
    entry.estimatedSeconds ? `预计 ${formatPlannedDuration(entry.estimatedSeconds)}` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
  /**
   * 这一行**要不要渲染验收动作** —— 判据在 `@fht/shared`
   * （`status === 'submitted' && canApprove`，与「今天的作业」**同一条**）。
   */
  const approval = homeworkApprovalActions({
    status: entry.status,
    canApprove: entry.canApprove === true,
    pointsPerUnit: entry.pointsPerUnit ?? null,
  });
  /**
   * 这一行**实际会不会**渲染出按钮：上面的判据 **再与"回调有没有给"取交"**
   * （学生端与只读家长刻意不传回调，见 `OverdueRowProps`）。
   *
   * ⚠️ 下面那行字（"等家长验收…"）与它**读的是同一个变量**：
   * 2026-10-15 用户实测的 bug 就是"字说点通过，按钮却不存在"。
   * 所以这里**只留一个**布尔量 —— 两处各写一遍判断，迟早又会对不上。
   */
  const showApprovalActions =
    approval.showApproveActions && Boolean(onApprove) && Boolean(onReject);
  /**
   * ⚠️ 超时秒数与"要不要显示"都走 `@fht/shared` 的判据（见 `homework-overdue.ts`）：
   * 已经是终态（家长刚点了放弃 / 孩子刚被验收通过）的项**不再显示**"已超时"，
   * 否则会出现"已放弃 🚫 + 已超时 3 小时"这种自相矛盾的一行。
   * 「还有效」那一支恒为 null（服务端给这些行的 `overdueSeconds` 是 0，
   * 它们按定义都还没到期）—— 这里再显式判一次 `isOpen`，是为了让"这一支永不说超时"
   * 成为**结构**上的保证，而不是靠"服务端刚好算成 0"。
   */
  const overdueSeconds = isOpen ? null : homeworkOverdueSeconds(entry);
  /**
   * 有没有**任何一个**操作按钮会渲染出来。
   *
   * ⚠️ 用它决定要不要渲染那一整块按钮容器，而不是只判断 `actionable`：
   * 家长端不传 `onPause` / `onResume` / `onFinish`（那一页没有计时的入口），
   * 于是一条正在计时的项会渲染出一个**空的** `mt-3` 区块 —— 白占一截高度，
   * 看起来像"按钮没加载出来"。
   */
  const showStart = actionable && entry.status === 'pending' && Boolean(onStart);
  const showRunning = actionable && isRunning && Boolean(onPause) && Boolean(onFinish);
  const showPaused = actionable && isPaused && Boolean(onResume) && Boolean(onFinish);
  const showSkip = Boolean(onSkip) && canSkip;
  const hasActions = showStart || showRunning || showPaused || showSkip;

  /**
   * 「放弃」按钮本体 —— **同一个按钮只有一个定义**，两处调用只是传**不同的 size**：
   *
   *   - 进验收面板的按钮行（`trailingActions`）→ 传 `APPROVAL_ACTION_BUTTON_SIZE`（`sm`）：
   *     必须与同行的「通过 / 打回」**一样高**。2026-10-15 用户实测的第三次修复就是这里：
   *     「通过 / 打回」是 `sm`（36px）而这个按钮是 `md`（44px），三个顶端对齐、底端差 8px，
   *     用户看到的就是"'放弃'比另外两个高"。⛔ 别在这里写 `'md'`。
   *   - 留在下面那个按钮块里 → `kid` 模式 `'kid'`、否则 `'md'`：那里的邻居是
   *     「现在做 / 暂停 / 完成」，本来就是 `md`（`kid` 模式是 56px 的大点击区），
   *     跟着它们走才是"一行里一样高"。
   *
   * ⚠️ 尺寸作为参数而不是两个 `const`：按钮的**文案 / 颜色 / 图标 / 禁用条件**
   * 因此仍然只有一份（两处各写一遍 `Button` 迟早会"样式或文案只改了一处"）。
   *
   * ⚠️ `variant="danger"`（红）与 `icon` 里的 🚫 都是**原件**，一个字没动：
   * 这次只统一**尺寸**，"放弃是危险操作"的视觉信号不能因为"看起来更整齐"而削弱。
   */
  const renderSkipButton = (size: ButtonSize) =>
    showSkip ? (
      <Button
        size={size}
        variant="danger"
        disabled={busy}
        icon={<span aria-hidden="true">🚫</span>}
        onClick={onSkip}
      >
        放弃
      </Button>
    ) : null;

  /**
   * 三按钮同属一行时，在「打回」与「放弃」之间插一条细竖线：
   * 左边两个是**验收**（通过 / 打回），右边那个是**收掉这一项**，语义不同
   * （见文件头"验收 vs 放弃"）。⚠️ 只影响观感，不改变任何动作或显隐。
   *
   * ⚠️ 它靠 `self-stretch` 量高度：三个按钮**一样高**时它就是那条按钮行的高度
   * （36px）；尺寸不统一时它会跟着**最高**的那个走（44px），看着像"分隔线比按钮高"。
   */
  const skipDivider = <span aria-hidden="true" className="w-px self-stretch bg-amber-300" />;

  return (
    <li>
      <div
        className={cn(
          'rounded-2xl border-2',
          // 「还有效」用天蓝而不是琥珀：琥珀在这一页的语义是"欠账/危险"，
          // 而这一支的每一项都**还没到期**，染成琥珀等于冤枉孩子。
          isOpen ? 'border-sky-300 bg-sky-50/40' : 'border-amber-300 bg-amber-50/40',
          kid ? 'p-4' : 'p-3',
          isRunning && 'ring-2 ring-brand-100',
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span aria-hidden="true" className={cn(kid ? 'text-2xl' : 'text-lg')}>
            {isOpen ? '📌' : '⏰'}
          </span>
          <p
            className={cn(
              'min-w-0 flex-1 font-semibold text-slate-900',
              kid ? 'text-xl' : 'text-base',
            )}
          >
            {entry.habitName}
          </p>
          <Badge tone={meta.tone}>
            {meta.icon} {meta.label}
          </Badge>
        </div>

        {/* 科目 · 属于哪一天 · 什么时候截止 —— 跨天累积的项必须看得出"出处"。
            「还有效」那一支把后两项换成服务端算好的徽章：
            `📅 周五 10-16 布置 · 周日 10-18 截止` —— 它**不是今天布置的**，
            把布置日说清楚是任务点名的一条；而那个徽章还刻意**不含时刻**
            （绝不说 23:59，见 `homeworkDueDateLabel` / `describeHomeworkOpenBadge` 的注释）。 */}
        <p className={cn('mt-1 text-slate-600', kid ? 'text-base' : 'text-xs')}>
          {isOpen
            ? [entry.subjectName, openBadge ?? `${entry.date} 的作业`].filter(Boolean).join(' · ')
            : [entry.subjectName, `${entry.date} 的作业`, `${formatDateTimeLocal(entry.dueAt)} 截止`]
                .filter(Boolean)
                .join(' · ')}
        </p>

        {overdueSeconds !== null && (
          <p className={cn('mt-1 font-semibold text-amber-800', kid ? 'text-base' : 'text-sm')}>
            已超时 {formatOverdueDuration(overdueSeconds)}
            {/* 「已经做了」是**实际用时**（actualSeconds）→ shared 的 formatDuration：
                做了 45 秒就写"45 秒"，不再四舍五入成"0 分钟"（见 duration.ts）。
                ⚠️ 有计时面板时**不再**重复说一遍（面板上就是大号数字，同一个数
                用两种格式显示两遍只会让人怀疑哪个准）。 */}
            {!showTimer && entry.actualSeconds > 0
              ? ` · 已经做了 ${formatDuration(entry.actualSeconds)}`
              : ''}
          </p>
        )}

        {/* ---------- 计时中 / 暂停中：大号已用时间（2026-10-17 修的 bug） ----------
            用户原话：
              > 孩子端这里点了暂停后，过一段时间想重新开始，就变成这样，
              > **看不到那个计时的了**。
            截图里那一项是「计时中」+「暂停 / 完成」，**唯独没有那个大的已用时间** ——
            因为这块 JSX 当时只存在于「今天的作业」与今日打卡页，两条队列行**没抄**。
            现在它与那两处渲染的是**同一个组件**（`CheckinTimerPanel`），
            所以"还有效 / 超时"的每一行与"今天的作业"的行有**完全一样**的计时体验：
            大号数字、`正在计时 / 已暂停` 说明、心跳失败提示、超过两倍提醒、到点提示音。

            ⚠️ `tone` 只改颜色（还有效 `sky` / 超时 `amber` / 当天 `brand`），
            ⛔ 它不是"另一份实现"的开关。
            ⚠️ `live` 由调用方给：学生端 `true`（数字会走、会发心跳），
            家长端默认 `false`（只读，心跳会 403）。 */}
        <CheckinTimerPanel
          checkinId={entry.id}
          status={entry.status}
          actualSeconds={entry.actualSeconds}
          estimatedSeconds={entry.estimatedSeconds ?? null}
          kid={kid}
          tone={isOpen ? 'sky' : 'amber'}
          live={live}
        />

        {/* 家长已经放弃过、但这一行还留在缓存里：说清"不用再做了"，别让人以为还能点 */}
        {entry.status === 'skipped' && (
          <p className={cn('mt-1 text-slate-500', kid ? 'text-base' : 'text-xs')}>
            🚫 家长已放弃这一项，不用再做了
            {entry.skipReason ? ` · ${entry.skipReason}` : ''}
          </p>
        )}

        {/* ---------- 打回原因 / 家长留言 ----------
            ⚠️ 这一行**以前只存在于「今天的作业」里**（见 `HomeworkItemRow` 的
            "家长说：…"），于是孩子从「超时 / 还有效」看那一项时**完全看不到**
            "为什么被打回、要改什么" —— 而这两个视图恰恰是打回后最可能待的地方
            （打回 ⇒ `pending`，队列行留在原处）。文案与那一处逐字一致。 */}
        {!isSubmitted && entry.status !== 'skipped' && entry.note && (
          <p className="mt-1 text-xs text-rose-600">家长说：{entry.note}</p>
        )}

        {/* ---------- 分值 / 目标量 / 预计时长 ----------
            与「今天的作业」的行**同一组信息**（取值范围也一致）：
            孩子在这里要判断"这一项值多少分、要做多少"，只给一个名字是不够的。
            `pointsPerUnit` 缺席（`null`）时**不说**"不记分" —— `null` 是"拿不到"，
            `0` 才是"明确不计分"（见 `HomeworkOverdueEntry.pointsPerUnit` 的注释）。
            ⚠️ `targetValue` / `unit` 由队列接口带上来（同一批作业项），
            「完成」弹窗据它们决定"完成量必填"（见 `lib/finishAmount.ts`）。 */}
        {entryInfoLine !== '' && (
          <p className={cn('mt-1 text-slate-500', kid ? 'text-base' : 'text-xs')}>{entryInfoLine}</p>
        )}

        {/* 「仍然可以做」的正面说明 —— 不能只靠颜色表达。
            两条队列各有一句（「还有效」那句来自 shared 的 `describeHomeworkOpenQueueText()`，
            两端逐字一致）：超时那一句说的是"做完它才会从超时里消失"，
            还有效那一句说的是"还没到期，现在就能做" —— 后一句⛔ 不许带催促。

            家长端要换一种说法：这一页**没有**开始/完成按钮（家长的主路径是
            "看进度 + 决定验收或放弃"，要动手就翻到那一天去），照着念"点完成交给家长验收"
            会让人找不到按钮。 */}
        {actionable && (
          <p className={cn('mt-1 text-xs', isOpen ? 'text-sky-800' : 'text-amber-700')}>
            {isOpen
              ? describeHomeworkOpenQueueText(parentView)
              : parentView
                ? '孩子那边仍然可以做（做完点「完成」交给你验收）；一直不做的话，就点「放弃」把它收掉。'
                : '仍然可以做：做完点「完成」交给家长验收，这一项才会从超时里消失。'}
          </p>
        )}
        {/*
          ⚠️ 这一行**必须与"有没有通过按钮"一致**（2026-10-15 用户实测的 bug 就是
          这里写着"点通过后会消失"、而下面**没有**通过按钮）：
            - 有验收动作（家长 + 有权限）→ 就直说"点通过后会消失"；
            - 没有（学生端 / 只读家长）→ 说清**为什么**（谁要动手、点哪里），
              绝不承诺一个这一屏上不存在的按钮。
        */}
        {isSubmitted && (
          <p className={cn('mt-1 text-xs', isOpen ? 'text-sky-800' : 'text-amber-800')}>
            {showApprovalActions
              ? isOpen
                ? '📤 已经交给家长了，等你验收；这一项会在你点「通过」后从「还有效」里消失。'
                : '📤 已经交给家长了，等你验收；这一项会在你点「通过」后从超时里消失。'
              : parentView
                ? '📤 已经交给家长了，等家长验收。你在这个孩子上的权限是只读，' +
                  '「通过 / 打回」要由可编辑的家长来点；在此之前它会一直留在这里。'
                : isOpen
                  ? '📤 已经交给家长了，等家长验收；家长点「通过」后这一项才会从「还有效」里消失。'
                  : '📤 已经交给家长了，等家长验收；家长点「通过」后这一项才会从超时里消失。'}
          </p>
        )}

        {/*
          验收动作：与「今天的作业」里那条作业项**同一个组件**（`ApprovalActions`），
          所以显隐判据、文案、按钮样式两处不可能漂移。

          ⚠️ 这一行里**没有第二个按钮容器**：「放弃」作为 `trailingActions` 进了面板的
          按钮行 —— 三个按钮因此**同属一行**（2026-10-15 第二次用户实测：它们当时
          排成了两行，因为"每一组动作各自一个 flex 容器"）。
          并且它用的是 `APPROVAL_ACTION_BUTTON_SIZE`，与同行的「通过 / 打回」**同高**
          （2026-10-15 第三次用户实测："放弃比另外两个高" —— 根因就是这里传的是 `md`）。
          ⛔ 别再把「放弃」拆回下面那块：只要按钮行分裂成两个容器，"排成两行"就会回来。
          ⛔ 也别改它的 size：同行不同 size 会让"高出来一截"回来。
        */}
        {showApprovalActions && (
          <ApprovalActions
            target={{
              status: entry.status,
              canApprove: true,
              pointsPerUnit: entry.pointsPerUnit ?? null,
            }}
            busy={busy}
            onApprove={() => onApprove?.()}
            onReject={() => onReject?.()}
            trailingActions={
              showSkip ? (
                <>
                  {skipDivider}
                  {renderSkipButton(APPROVAL_ACTION_BUTTON_SIZE)}
                </>
              ) : null
            }
          />
        )}

        {/* ⛔ 这里**不要**再写一句"正在计时中 / 已暂停"。
            那句话曾经是本行唯一的计时反馈（因为没有面板），现在计时面板里有
            `正在计时 / 已暂停，点「继续」接着算`（与「今天的作业」逐字一致），
            两句并存就是同一个状态说两遍。 */}

        {hasActions && (
          <div className={cn('mt-3 flex flex-wrap gap-2', kid && 'gap-3')}>
            {showStart && (
              <Button
                size={kid ? 'kid' : 'md'}
                variant={kid ? 'kid' : 'primary'}
                disabled={busy}
                icon={<span aria-hidden="true">▶️</span>}
                onClick={onStart}
              >
                现在做
              </Button>
            )}
            {showRunning && (
              <>
                <Button size={kid ? 'kid' : 'md'} variant="secondary" disabled={busy} onClick={onPause}>
                  暂停
                </Button>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid-green' : 'primary'}
                  disabled={busy}
                  onClick={onFinish}
                >
                  完成
                </Button>
              </>
            )}
            {showPaused && (
              <>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid' : 'primary'}
                  disabled={busy}
                  onClick={onResume}
                >
                  继续
                </Button>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid-green' : 'primary'}
                  disabled={busy}
                  onClick={onFinish}
                >
                  完成
                </Button>
              </>
            )}

            {/*
              「放弃」：家长端专属，而且**只有传了 onSkip 才存在**。
              ⛔ 学生端调用方刻意不传这个 prop —— 后端对作业项的 skip 返回
              `HOMEWORK_SKIP_PARENT_ONLY`(403)，但界面不靠"点了会失败"来保护。
              文案与"这一项会留在超时里清不掉"是同一个问题的两面：
              没有这个按钮，孩子永远不做的那一项就真的清不掉了。

              ⚠️ 渲染位置与验收动作**互斥**：这一行要渲染验收面板时，「放弃」由
              `renderSkipButton` 交给那个面板的按钮行（三个按钮同一行**同一高度**，见
              `ApprovalActions` 文件头"必须同 size"）；只有**没有**验收面板时
              （学生端没有 onSkip、只读家长没有权限、或状态不是 submitted）
              才留在这个块里，与「开始 / 暂停 / 完成」排在一起。
              ⚠️ 这里传的 size 与面板那一行**不同**（`md` / `kid`）：这一块的邻居是
              `md` 的「现在做 / 暂停 / 完成」，跟着它们才是一样高。
              ⛔ 别把它改回"两处都渲染"—— 那就是同一行里出现两个「放弃」。
            */}
            {showSkip && !showApprovalActions && renderSkipButton(kid ? 'kid' : 'md')}
          </div>
        )}
      </div>
    </li>
  );
}
